// Source filters (GUNDI-5178) — the whitelist/blacklist rules on a route's arrows.
//
// A rule lives on one route→destination pair and says which devices may send data
// there. Whitelist and blacklist are mutually exclusive, enforced by a database
// constraint: a destination carries one rule of a kind or none, never both. So the
// editor picks a mode for the whole set rather than marking devices individually.

export type SourceFilterMode = 'whitelist' | 'blacklist';

// A device, as the rule and the picker both see it. `id` is what the rule stores —
// `external_id` is only unique within a provider, so it cannot identify a device on
// its own.
export type SourceSummary = {
  id: string;
  external_id: string;
  name: string;
  // "unknown" for a device that has never reported.
  last_update: string | null;
  // Immutable origin: "manual" was typed in by a user, "ingestion" was discovered
  // when the integration first sent data. Optional because the rule's own sources
  // sub-endpoint may lag behind the catalogue endpoint in exposing it.
  created_via?: 'ingestion' | 'manual';
};

export type ProviderSummary = {
  id: string;
  name: string;
};

export type SourceFilter = {
  id: string;
  destination: { id: string; name: string };
  type: string;
  mode: SourceFilterMode;
  enabled: boolean;
  name: string;
  description: string;
  sources_count: number;
  // The providers the rule actually covers. A rule only affects the providers whose
  // sources it names, and a client holding one page of devices cannot work that out
  // for itself — without this, a multi-provider route gives no clue why one feed is
  // untouched.
  providers: ProviderSummary[];
  // True when an allowed list leaves out the external_id ingestion assigns to
  // submissions that don't name a device ("default-source") — the data behind
  // the §10 advisory, computed server-side. Optional: older cached payloads
  // predate it and must keep rendering.
  excludes_default_source?: boolean;
  updated_at: string;
};

// What the editor holds while the user works. Writes are whole-list — there is no
// per-device add or remove endpoint — so the draft carries the complete set and the
// save sends it in one request.
export type SourceFilterDraft = {
  mode: SourceFilterMode;
  sources: SourceSummary[];
};

// New rules default to blacklist so creating or opening one never cuts data that
// already flows: everything keeps reaching until the user marks a device.
export const emptyDraft = (): SourceFilterDraft => ({
  mode: 'blacklist',
  sources: [],
});

export const draftFromFilter = (
  filter: SourceFilter,
  sources: SourceSummary[],
): SourceFilterDraft => ({ mode: filter.mode, sources });

export const hasNeverReported = (source: SourceSummary): boolean =>
  !source.last_update || source.last_update === 'unknown';

// The badge condition. created_via is authoritative and immutable; guessing from
// never-reported would badge a just-created ingestion device as manual and
// contradict the drawer's Origin field on the same row.
export const isManuallyAdded = (source: SourceSummary): boolean =>
  source.created_via === 'manual';

export const sourceFilterKey = (destinationId: string, direction: string) =>
  `${destinationId}|${direction}`;
